import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi'
import { getNotifications, markNotificationAsRead } from './Notification.slice'
import NotificationDetailDrawer, { DRAWER_ID } from './NotificationDetailDrawer'
import {
  getNotificationMessage,
  getNotificationSubject,
} from './buildNotificationDetail'
import { scrollToPensionHero } from '../Dashboard/pensionHeroAnchors'
import {
  ListRoot,
  ListSummary,
  SummaryText,
  UnreadCount,
  ListBody,
  NotificationItem,
  NotificationContent,
  NotificationTop,
  NotificationText,
  TimeText,
  UserName,
  UnreadDot,
  PaginationBar,
  PaginationInfo,
  PaginationControls,
  PageButton,
  EmptyState,
  EmptyMessage,
  EmptyHint,
  SkeletonItem,
  SkeletonDot,
  SkeletonLines,
  SkeletonLine,
} from './Notification.styles'

const formatRelativeTime = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''

  const diffMs = Date.now() - date.getTime()
  const minutes = Math.floor(diffMs / 60000)

  if (minutes < 1) return 'Ahora'
  if (minutes < 60) return `Hace ${minutes} min`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `Hace ${hours} h`

  const days = Math.floor(hours / 24)
  if (days < 7) return `Hace ${days} d`

  return date.toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const LoadingSkeleton = () => (
  <ListBody>
    {[0, 1, 2].map((key) => (
      <SkeletonItem key={key}>
        <SkeletonDot />
        <SkeletonLines>
          <SkeletonLine $width="55%" $height="0.85rem" />
          <SkeletonLine $width="88%" />
          <SkeletonLine $width="35%" $height="0.65rem" />
        </SkeletonLines>
      </SkeletonItem>
    ))}
  </ListBody>
)

const NotificationList = () => {
  const dispatch = useDispatch()
  const userId = useSelector((state) => state.adminAuth.user?.id)
  const {
    notifications = [],
    page = 0,
    pageSize = 5,
    total = 0,
    status,
  } = useSelector((state) => state.notifications)
  const [selectedNotification, setSelectedNotification] = useState(null)

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const hasPrev = page > 0
  const hasNext = page + 1 < totalPages
  const isLoading = status.fetch === 'loading'
  const unreadOnPage = notifications.filter((n) => n.status === 'unread').length
  const rangeStart = total === 0 ? 0 : page * pageSize + 1
  const rangeEnd = Math.min(total, (page + 1) * pageSize)

  const goToPage = (nextPage) => {
    if (nextPage < 0 || nextPage >= totalPages || nextPage === page) return
    dispatch(getNotifications(nextPage))
  }

  const handleNotificationClick = (notif) => {
    setSelectedNotification(notif)
    if (notif.status === 'unread') {
      dispatch(markNotificationAsRead(notif.id))
    }
  }

  const handleDrawerClose = () => {
    setSelectedNotification(null)
  }

  const handleDrawerAction = (cta) => {
    scrollToPensionHero({ focusPayment: Boolean(cta?.focusPayment) })
  }

  useEffect(() => {
    if (!userId) return undefined
    dispatch(getNotifications(0))
    return undefined
  }, [dispatch, userId])

  if (isLoading && notifications.length === 0) {
    return (
      <ListRoot>
        <LoadingSkeleton />
      </ListRoot>
    )
  }

  if (status.fetch === 'succeeded' && notifications.length === 0) {
    return (
      <ListRoot>
        <EmptyState>
          <EmptyMessage>Todo al día</EmptyMessage>
          <EmptyHint>
            Cuando haya novedades sobre tu cuenta o pensión, aparecen aquí.
          </EmptyHint>
        </EmptyState>
      </ListRoot>
    )
  }

  return (
    <ListRoot>
      <ListSummary>
        <SummaryText>
          {rangeStart}–{rangeEnd} de {total}
        </SummaryText>
        {unreadOnPage > 0 ? (
          <UnreadCount>
            <UnreadDot $unread $compact aria-hidden />
            {unreadOnPage} sin leer
          </UnreadCount>
        ) : (
          <SummaryText>Todo al día</SummaryText>
        )}
      </ListSummary>

      <ListBody>
        {notifications.map((notif) => {
          const isUnread = notif.status === 'unread'
          const isSelected = selectedNotification?.id === notif.id
          const subject = getNotificationSubject(notif)
          const message = getNotificationMessage(notif)

          return (
            <NotificationItem
              key={notif.id}
              type="button"
              $unread={isUnread}
              $selected={isSelected}
              onClick={() => handleNotificationClick(notif)}
              aria-expanded={isSelected}
              aria-controls={DRAWER_ID}
              aria-label={`Ver notificación: ${subject}`}
            >
              <UnreadDot $unread={isUnread} aria-hidden />
              <NotificationContent>
                <NotificationTop>
                  <UserName $unread={isUnread}>{subject}</UserName>
                  <TimeText $unread={isUnread}>
                    {formatRelativeTime(notif.created_at)}
                  </TimeText>
                </NotificationTop>

                {message ? (
                  <NotificationText $unread={isUnread}>
                    {message}
                  </NotificationText>
                ) : null}
              </NotificationContent>
            </NotificationItem>
          )
        })}
      </ListBody>

      {total > pageSize && (
        <PaginationBar>
          <PaginationInfo>
            Página {page + 1} de {totalPages}
          </PaginationInfo>
          <PaginationControls>
            <PageButton
              type="button"
              disabled={!hasPrev || isLoading}
              onClick={() => goToPage(page - 1)}
              aria-label="Página anterior"
            >
              <HiChevronLeft size={16} />
              Anterior
            </PageButton>
            <PageButton
              type="button"
              disabled={!hasNext || isLoading}
              onClick={() => goToPage(page + 1)}
              aria-label="Página siguiente"
            >
              Siguiente
              <HiChevronRight size={16} />
            </PageButton>
          </PaginationControls>
        </PaginationBar>
      )}

      <NotificationDetailDrawer
        notification={selectedNotification}
        onClose={handleDrawerClose}
        onAction={handleDrawerAction}
      />
    </ListRoot>
  )
}

export default NotificationList
